/**
 * Client read-side classification for purchase receipts.
 *
 * Stock-in is only a direction: transfers, production outputs, inventory
 * adjustments, corrections and reversals are stock-in too, but they are not
 * purchases. Food Cost and traceability must therefore share this stricter
 * classifier instead of treating every positive movement as a supplier load.
 */

export interface PhysicalPurchasePayload {
  [key: string]: unknown;
  business_id?: unknown;
  movement_class?: unknown;
  economic_effect?: unknown;
  command_id?: unknown;
  reason?: unknown;
  supplier_id?: unknown;
  line_total?: unknown;
  cost_source?: unknown;
  qty?: unknown;
  stock_qty?: unknown;
  document_id?: unknown;
  document_number?: unknown;
  transfer_from_business_id?: unknown;
  transfer_to_business_id?: unknown;
  source_production_id?: unknown;
  finished_good?: unknown;
  inventory_delta?: unknown;
  correction_leg?: unknown;
  corrects_event_id?: unknown;
  uom_rebase_source?: unknown;
  uom_rebase_snapshot?: unknown;
  uom_rebase_zero?: unknown;
  lot_state_action?: unknown;
  void_compensation?: unknown;
  compensation?: unknown;
  reverses_event_id?: unknown;
  reverses_event_client_uuid?: unknown;
  compensates_event_id?: unknown;
  compensates_event_client_uuid?: unknown;
  cancels_event_id?: unknown;
  cancels_event_client_uuid?: unknown;
  cancelled_event_id?: unknown;
  cancelled_event_client_uuid?: unknown;
}

export interface PhysicalPurchaseEvent {
  id?: string | null;
  server_id?: string | null;
  client_uuid?: string | null;
  server_rev?: number | null;
  rev?: number | null;
  server_ts?: string | null;
  client_ts?: string | null;
  kind?: string | null;
  business_id?: string | null;
  payload?: PhysicalPurchasePayload | null;
}

const REVERSAL_REFERENCE_FIELDS = [
  'reverses_event_id',
  'reverses_event_client_uuid',
  'compensates_event_id',
  'compensates_event_client_uuid',
  'cancels_event_id',
  'cancels_event_client_uuid',
  'cancelled_event_id',
  'cancelled_event_client_uuid',
] as const;

function cleanString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.trim();
  return clean || null;
}

function hasOwn(
  value: object,
  key: PropertyKey,
): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function reversalReferences(
  payload: Readonly<PhysicalPurchasePayload>,
): string[] {
  return REVERSAL_REFERENCE_FIELDS
    .map((field) => cleanString(payload[field]))
    .filter((reference): reference is string => reference !== null);
}

function hasReversalMetadata(
  payload: Readonly<PhysicalPurchasePayload>,
): boolean {
  return payload.void_compensation === true
    || payload.compensation === true
    || reversalReferences(payload).length > 0;
}

function hasTechnicalMetadata(
  payload: Readonly<PhysicalPurchasePayload>,
): boolean {
  return payload.transfer_from_business_id != null
    || payload.transfer_to_business_id != null
    || payload.source_production_id != null
    || payload.finished_good === true
    || payload.inventory_delta != null
    || payload.correction_leg != null
    || payload.corrects_event_id != null
    || payload.uom_rebase_source === true
    || payload.uom_rebase_snapshot === true
    || payload.uom_rebase_zero === true
    || cleanString(payload.lot_state_action) !== null;
}

function excludedLegacyReason(reason: string): boolean {
  return reason === 'quick_label_receive'
    || reason === 'replace_active_lot'
    || reason === 'lot_correction'
    || reason === 'lot_closed'
    || reason === 'lot_set_active'
    || reason === 'lot_state_updated'
    || reason === 'expiration_date_correction'
    || reason === 'manual_stock_out'
    || reason.includes('transfer')
    || reason.includes('production')
    || reason.includes('inventory')
    || reason.includes('reverse')
    || reason.includes('void')
    || reason.includes('cancel')
    || reason.includes('rebase')
    || reason.includes('correction')
    || reason.includes('corrected')
    || reason.includes('reopen');
}

function hasExplicitNonPositiveQuantity(
  payload: Readonly<PhysicalPurchasePayload>,
): boolean {
  const raw = hasOwn(payload, 'stock_qty') ? payload.stock_qty : payload.qty;
  if (raw == null) return false;
  const quantity = typeof raw === 'number'
    ? raw
    : typeof raw === 'string' && raw.trim() !== ''
      ? Number(raw)
      : Number.NaN;
  return Number.isFinite(quantity) && quantity <= 0;
}

/**
 * New events must explicitly be `receive + purchase`.
 *
 * The legacy compatibility path is intentionally narrower: it needs document
 * evidence, an explicit manual cost source, or one of the two historical
 * receive reasons. A carried supplier alone is not evidence of a purchase
 * because old transfer/correction legs may retain that snapshot.
 */
export function isPhysicalPurchaseReceiptPayload(
  payload: Readonly<PhysicalPurchasePayload> | null | undefined,
): boolean {
  if (
    !payload
    || hasReversalMetadata(payload)
    || hasTechnicalMetadata(payload)
    || hasExplicitNonPositiveQuantity(payload)
  ) {
    return false;
  }

  const movementClass = cleanString(payload.movement_class);
  const economicEffect = cleanString(payload.economic_effect);
  const hasSemanticEnvelope = hasOwn(payload, 'movement_class')
    || hasOwn(payload, 'economic_effect')
    || hasOwn(payload, 'command_id');
  if (hasSemanticEnvelope) {
    return movementClass === 'receive' && economicEffect === 'purchase';
  }

  const reason = cleanString(payload.reason)?.toLowerCase() ?? '';
  if (excludedLegacyReason(reason)) return false;
  const hasDocumentEvidence = cleanString(payload.document_id) !== null
    || cleanString(payload.document_number) !== null;
  return hasDocumentEvidence
    || cleanString(payload.cost_source)?.toLowerCase() === 'manual'
    || reason === 'document_import'
    || reason === 'manual_receive';
}

function eventIdentities(event: Readonly<PhysicalPurchaseEvent>): string[] {
  return [event.id, event.server_id, event.client_uuid]
    .map(cleanString)
    .filter((identity): identity is string => identity !== null);
}

function eventRevision(event: Readonly<PhysicalPurchaseEvent>): number | null {
  const value = event.server_rev ?? event.rev;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function eventTimestamp(event: Readonly<PhysicalPurchaseEvent>): number | null {
  const raw = cleanString(event.server_ts) ?? cleanString(event.client_ts);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function reversalIsLater(
  receipt: Readonly<PhysicalPurchaseEvent>,
  reversal: Readonly<PhysicalPurchaseEvent>,
): boolean {
  const receiptRev = eventRevision(receipt);
  const reversalRev = eventRevision(reversal);
  if (receiptRev !== null && reversalRev !== null) {
    return reversalRev > receiptRev;
  }
  if (receiptRev !== null || reversalRev !== null) {
    // A mixed optimistic/authoritative pair cannot establish that the
    // reference predates the receipt. The explicit reference therefore wins.
    return true;
  }
  const receiptTs = eventTimestamp(receipt);
  const reversalTs = eventTimestamp(reversal);
  if (receiptTs !== null && reversalTs !== null) {
    return reversalTs >= receiptTs;
  }
  // An explicit reference is authoritative when one side is still optimistic
  // and therefore lacks a server revision/timestamp.
  return true;
}

/**
 * Returns only purchase receipts that remain active after later stock-out
 * reversals. Both local client UUID (`id`) and authoritative `server_id` are
 * matched because events_local stores those identities separately.
 */
export function selectActivePhysicalPurchaseReceipts<
  Receipt extends PhysicalPurchaseEvent,
>(
  stockInEvents: readonly Receipt[],
  stockOutEvents: readonly PhysicalPurchaseEvent[],
): Receipt[] {
  const reversalsByReference = new Map<string, PhysicalPurchaseEvent[]>();
  for (const event of stockOutEvents) {
    if (event.kind != null && event.kind !== 'stock_out') continue;
    const payload = event.payload;
    if (!payload) continue;
    const references = reversalReferences(payload);
    if (references.length === 0) continue;
    for (const reference of references) {
      const reversals = reversalsByReference.get(reference) ?? [];
      reversals.push(event);
      reversalsByReference.set(reference, reversals);
    }
  }

  return stockInEvents.filter((receipt) => {
    if (receipt.kind != null && receipt.kind !== 'stock_in') return false;
    if (!isPhysicalPurchaseReceiptPayload(receipt.payload)) return false;
    return !eventIdentities(receipt).some((identity) =>
      (reversalsByReference.get(identity) ?? []).some((reversal) =>
        reversalIsLater(receipt, reversal)
      )
    );
  });
}
